import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import useProductBasicDetails from 'data-hooks/useProductBasicDetails';
import usePDPPhotos from 'data-hooks/usePDPPhotos';
import useProductAvailableSizes from 'data-hooks/useProductAvailableSizes';
import useProductCardOptions from 'data-hooks/useProductCardOptions';
import { getConfigPhotosForSelectedOptions } from 'data-hooks/usePDPPhotos/utils';
import { getOptionAdditionalPrice } from 'data-hooks/useProductPrice/utils';
import { PRODUCT_OPTION_URL_SUFFIX } from 'commons/constants';
import useProductAvailabilityByStock from 'components/consumer/product-card/hooks/useProductAvailabilityByStock';
import PDPAvailableInSizesRowUI from './PDPAvailableInSizesRowUI';
import usePDPOptions from '../../state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from '../../state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
const queryOptions = {
  ssr: false
};
const PDPAvailableInSizesRow = ({
  isActive,
  slug,
  id,
  pdpProductId,
  removeUnavailableProductId,
  productsData = []
}) => {
  const {
    loading: stockStatusLoading,
    isProductOOS,
    isProductDiscontinued,
    isMarkedOOSOrDiscontinued
  } = useProductAvailabilityByStock(id, slug, {
    hasStockData: false,
    allowDiscontinuedRemoval: true
  });

  // Note: while this component is within PDP, it is used to render details
  // about other products so cannot use product-specific data from PDP context
  const {
    loading: basicDetailsLoading,
    data: basicDetails
  } = useProductBasicDetails(id, slug, queryOptions);
  const {
    loading: photosLoading,
    data: photosData
  } = usePDPPhotos(id, slug, {
    ...queryOptions,
    skipLocalStockHook: false
  });
  const {
    loading: optionsLoading,
    data: optionsData
  } = usePDPOptions();
  const {
    loading: sizeVariantOptionsLoading,
    data: sizeVariantOptionsData
  } = useProductCardOptions(id, slug, queryOptions);
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  const {
    upholsteryOptionId,
    selectedOptionsExtended
  } = optionsData;
  const displayPhoto = useMemo(() => {
    if (optionsLoading) {
      return photosData?.configurationPhotos?.[0]?.src;
    }
    const configPhotos = getConfigPhotosForSelectedOptions(selectedOptionsVariantSku, selectedOptionsExtended, upholsteryOptionId, basicDetails);
    return configPhotos?.[0]?.src || photosData?.configurationPhotos?.[0]?.src || '';
  }, [basicDetails, optionsLoading, photosData, selectedOptionsExtended, selectedOptionsVariantSku, upholsteryOptionId]);
  useEffect(() => {
    if (stockStatusLoading || !isMarkedOOSOrDiscontinued || !isProductOOS && !isProductDiscontinued) {
      return;
    }
    // Any products that are unavailable for purchase should not be shown
    removeUnavailableProductId(id);
  }, [stockStatusLoading, isMarkedOOSOrDiscontinued, isProductDiscontinued, isProductOOS, removeUnavailableProductId, id]);

  // Prefetching for smoother transition
  // eslint-disable-next-line no-unused-vars
  const availableSizesData = useProductAvailableSizes(id, slug);
  const rowSelectedOptions = useMemo(() => {
    if (!selectedOptionsExtended || !sizeVariantOptionsData?.options) return {};
    const mapping = {};
    Object.entries(selectedOptionsExtended).forEach(([optionId, optionData]) => {
      const selectedValueSku = optionData?.value?.sku;
      if (!selectedValueSku) return;
      const rowOption = sizeVariantOptionsData.options.find(opt => +opt.id === +optionId);
      if (!rowOption?.values) return;
      const matchingValue = rowOption.values.find(val => val.sku === selectedValueSku);
      if (matchingValue) {
        mapping[optionId] = matchingValue.id;
      }
    });
    return mapping;
  }, [selectedOptionsExtended, sizeVariantOptionsData?.options]);
  const rowOptionPrice = useMemo(() => {
    if (!sizeVariantOptionsData?.options) return 0;
    return getOptionAdditionalPrice(sizeVariantOptionsData.options, sizeVariantOptionsData.stock?.stockOptions, rowSelectedOptions, true, upholsteryOptionId);
  }, [sizeVariantOptionsData, rowSelectedOptions, upholsteryOptionId]);
  const priceWithFabricUpcharge = useMemo(() => {
    if (!basicDetails?.meta?.price) return 0;
    const originalPrice = basicDetails.meta.price.original_price || 0;
    const discountPercent = basicDetails.meta.price.discount_percent || 0;
    const totalBeforeDiscount = originalPrice + rowOptionPrice;
    if (discountPercent > 0) {
      return Math.round(totalBeforeDiscount * (100 - discountPercent) / 100);
    }
    return totalBeforeDiscount;
  }, [basicDetails?.meta?.price, rowOptionPrice]);
  const urlKey = useMemo(() => {
    if (!basicDetails || !pdpProductId) {
      return '';
    }
    if (!upholsteryOptionId || isEmpty(selectedOptionsExtended) || !selectedOptionsExtended?.[upholsteryOptionId]?.value?.value) {
      return basicDetails.attributes_min?.urlKey || '';
    }
    const optionValue = selectedOptionsExtended[upholsteryOptionId].value.value;
    const formattedOptionValue = optionValue.toLowerCase().split(' ').join('_');
    return `${basicDetails?.attributes_min?.urlKey || ''}?${PRODUCT_OPTION_URL_SUFFIX[upholsteryOptionId]}=${formattedOptionValue}`;
  }, [selectedOptionsExtended, upholsteryOptionId, basicDetails, pdpProductId]);
  return <PDPAvailableInSizesRowUI dimensions={basicDetails?.attributes_min?.dimensions} id={basicDetails?.id} image={displayPhoto} isActive={isActive} loading={basicDetailsLoading || photosLoading || sizeVariantOptionsLoading} name={basicDetails?.name} price={priceWithFabricUpcharge} productsData={productsData} urlKey={urlKey} />;
};
PDPAvailableInSizesRow.propTypes = {
  isActive: PropTypes.bool,
  slug: PropTypes.string,
  pdpProductId: PropTypes.number,
  id: PropTypes.number,
  removeUnavailableProductId: PropTypes.func,
  productsData: PropTypes.arrayOf(PropTypes.object)
};
export default PDPAvailableInSizesRow;